import { useId } from 'react';

interface FeatItIconProps {
  className?: string;
  variant?: 'color' | 'theme';
}

/**
 * The FEATit mark, drawn inline (I-1183). It was an `<img>` of `/featit-icon*.svg`, which the browser
 * asks for only when the mark mounts, so a row or a page was painted without it until the request
 * answered — the phone menu's Home row first appeared with no icon. Inline, it is drawn in the same
 * frame as whatever holds it, as every lucide icon is. The shapes are `public/featit-icon.svg`'s.
 */
export function FeatItIcon({ className, variant = 'color' }: FeatItIconProps) {
  // Gradient ids are document-wide: two marks on one page must not share them. `useId`'s own
  // punctuation is dropped so the id reads cleanly inside `url(#…)`.
  const id = `featit-mark-${useId().replace(/[^\w-]/g, '')}`;

  if (variant === 'theme') {
    // Black in the light theme, white in the dark, as the two theme files were.
    return (
      <svg
        viewBox="0 0 24 24"
        aria-hidden="true"
        data-featit-mark="theme"
        className={`shrink-0 text-black dark:text-white ${className ?? ''}`}
      >
        <path
          d="M12 2 20.66 7v10L12 22 3.34 17V7Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinejoin="round"
        />
        <circle cx="12" cy="12" r="5.1" fill="none" stroke="currentColor" strokeWidth="2.2" />
        <circle cx="12" cy="12" r="2.5" fill="currentColor" />
      </svg>
    );
  }

  const core = `${id}-core`;
  const ring = `${id}-ring`;
  const frame = `${id}-frame`;
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" data-featit-mark="color" className={className}>
      <defs>
        <radialGradient id={core} cx="40%" cy="36%" r="75%">
          <stop stopColor="#FFE397" />
          <stop offset=".5" stopColor="#FFBD53" />
          <stop offset="1" stopColor="#F28A2D" />
        </radialGradient>
        <radialGradient id={ring}>
          <stop offset=".55" stopColor="#C8DF69" />
          <stop offset=".8" stopColor="#99CF5C" />
          <stop offset="1" stopColor="#31A99E" />
        </radialGradient>
        <linearGradient id={frame} x2=".7" y2="1">
          <stop stopColor="#23B5C7" />
          <stop offset="1" stopColor="#3D67DE" />
        </linearGradient>
      </defs>
      <path
        d="M12 2 20.66 7v10L12 22 3.34 17V7Z"
        fill="none"
        stroke={`url(#${frame})`}
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="5.1" fill="none" stroke={`url(#${ring})`} strokeWidth="2.2" />
      <circle cx="12" cy="12" r="2.5" fill={`url(#${core})`} />
    </svg>
  );
}
